import { createAvatar } from '@dicebear/core'
import { adventurer, bottts, funEmoji, lorelei, pixelArt, thumbs } from '@dicebear/collection'

// Map of style IDs to their DiceBear modules
const STYLE_MAP = {
  adventurer,
  bottts,
  funEmoji,
  lorelei,
  pixelArt,
  thumbs,
} as const

type GameStyleId = keyof typeof STYLE_MAP

interface GameCharacterConfig {
  style: GameStyleId
  seed: string
  options?: Record<string, string | number>
}

/**
 * Parse game character config from JSON string
 */
export function parseGameCharacterConfig(configStr: string | undefined): GameCharacterConfig | null {
  if (!configStr) return null
  
  try {
    const parsed = JSON.parse(configStr)
    if (parsed.style && parsed.seed && parsed.style in STYLE_MAP) {
      return {
        style: parsed.style,
        seed: parsed.seed,
        options: parsed.options || {}
      }
    }
    return null
  } catch {
    return null
  }
}

/**
 * Generate a data URI for a game character avatar
 * This can be loaded into Phaser using: this.textures.addBase64('character', dataUri)
 * @param config - The parsed game character config
 * @param size - Size in pixels
 * @param transparent - If true, removes the background color
 * @param flipForGame - If true, flips the avatar to face right (for games that scroll left-to-right)
 */
export function generateGameCharacterDataUri(config: GameCharacterConfig, size = 128, transparent = false, flipForGame = false): string {
  const styleModule = STYLE_MAP[config.style]
  if (!styleModule) return ''
  
  // Convert options for DiceBear (wrap strings in arrays)
  const dicebearOptions: Record<string, unknown> = {}
  if (config.options) {
    for (const [key, value] of Object.entries(config.options)) {
      // Skip backgroundColor if we want transparent
      if (transparent && key === 'backgroundColor') {
        continue
      }
      if (key.endsWith('Probability')) {
        dicebearOptions[key] = value
      } else if (typeof value === 'string') {
        dicebearOptions[key] = [value]
      } else {
        dicebearOptions[key] = value
      }
    }
  }
  
  // Set transparent background if requested
  if (transparent) {
    dicebearOptions.backgroundColor = ['transparent']
  }
  
  // Flip avatar to face right for games
  if (flipForGame) {
    dicebearOptions.flip = true
  }
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return createAvatar(styleModule as any, {
    seed: config.seed,
    size,
    ...dicebearOptions,
  }).toDataUri()
}

/**
 * Generate game character data URI from raw config string
 * Convenience function that combines parsing and generation
 * @param configStr - JSON string of the game character config
 * @param size - Size in pixels
 * @param transparent - If true, removes the background color
 * @param flipForGame - If true, flips the avatar to face right (for games)
 */
export function getGameCharacterUri(configStr: string | undefined, size = 128, transparent = false, flipForGame = false): string | null {
  const config = parseGameCharacterConfig(configStr)
  if (!config) return null
  return generateGameCharacterDataUri(config, size, transparent, flipForGame)
}

/**
 * Load game character into Phaser texture manager
 * Call this in your preload() or create() scene method
 * 
 * Example usage:
 * ```
 * import { loadGameCharacterTexture } from '@/utils/gameCharacter'
 * 
 * // In your scene's create():
 * const childProfile = this.registry.get('childProfile')
 * loadGameCharacterTexture(this, childProfile?.gameCharacterConfig, 'playerAvatar')
 * 
 * // Then use it:
 * this.add.image(400, 300, 'playerAvatar')
 * ```
 */
export function loadGameCharacterTexture(
  scene: Phaser.Scene,
  configStr: string | undefined,
  textureKey: string,
  size = 128
): boolean {
  const dataUri = getGameCharacterUri(configStr, size)
  if (!dataUri) return false
  
  // Check if texture already exists
  if (scene.textures.exists(textureKey)) {
    scene.textures.remove(textureKey)
  }
  
  // Add base64 texture to Phaser
  scene.textures.addBase64(textureKey, dataUri)
  return true
}










